/**
 * Unified, white-label connector REST surface (May 2026).
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/connectors_unified.py``
 *   - ``ando-agent/src/api/v1/endpoints/openbanking.py``
 *     (``GET /connectors/open_banking/institutions`` lives here for
 *     historical reasons — the bank picker shares the openbanking router.)
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO" — every endpoint here is
 * addressed by *capability* (``crm``, ``accounting``, ``open_banking``,
 * …), never by vendor. The BE resolves the vendor server-side via
 * ``ConnectorFactory._resolve(tenant_id, capability)``; callers may supply
 * an explicit ``vendor`` override but should leave it unset by default.
 *
 * v1 source-of-truth: none — v1 spoke directly to per-vendor routes
 * (``/api/v1/salesforce/connect``, ``/api/v1/fic-auth/connect``, …). The
 * v2 ``capabilityApi`` adapter
 * (``apps/client/src/features/connectors/api/capabilityApi.ts``) is the
 * single mapping layer; this module is its typed transport.
 */

import { apiClient } from "../ApiClient.js";

// ---------------------------------------------------------------------------
// Capability whitelist — kept in sync with
// ``connectors_unified.SUPPORTED_CAPABILITIES``.
// ---------------------------------------------------------------------------

export type CapabilityName =
  | "crm"
  | "project_management"
  | "accounting"
  | "calendar"
  | "messaging"
  | "drive"
  | "email"
  | "open_banking"
  | "filesystem"
  | "aruba_pec"
  | "infocert_pec"
  | "sdi_invoicing"
  | "cassetto_fiscale"
  | "vat_verifier"
  | "legal_storage"
  | "peppol_gateway"
  | "invoice_extractor"
  | "cad_library";

// ---------------------------------------------------------------------------
// /start
// ---------------------------------------------------------------------------

/**
 * Generic credentials-form field descriptor, matching
 * ``connectors_unified._credentials_schema_for``. Mirrors the per-vendor
 * ``FieldSpec`` we expose for ``<ConnectorWizard>`` form steps.
 */
export interface ConnectorFormField {
  readonly key: string;
  readonly label: string;
  readonly type: "string" | "secret" | "number" | "boolean";
  readonly required?: boolean;
  readonly placeholder?: string;
  /** Optional helper copy rendered under the input (e.g. PSD2 OB
   *  explains the field accepts both P.IVA and Codice Fiscale). */
  readonly helperText?: string;
}

export interface ConnectorFormSchema {
  readonly fields: readonly ConnectorFormField[];
}

/**
 * Response of ``POST /connectors/{capability}/start``. The variant carried
 * by ``flow_type`` decides the wizard's next move:
 *
 *  - ``oauth_redirect``    — host page should ``window.location =`` ``oauth_url``.
 *  - ``credentials_form``  — wizard renders ``form_schema`` as a form step.
 *  - ``not_implemented``   — wizard shows a "coming soon" body with ``detail``.
 */
export type StartFlowResponse =
  | {
      readonly flow_type: "oauth_redirect";
      readonly oauth_url: string;
    }
  | {
      readonly flow_type: "credentials_form";
      readonly form_schema: ConnectorFormSchema;
    }
  | {
      readonly flow_type: "not_implemented";
      readonly detail?: string;
    };

export interface StartFlowBody {
  readonly access_mode?: "workspace" | "send_only";
  /** Optional explicit vendor override; otherwise BE picks the default. */
  readonly vendor?: string;
}

/** POST ``/connectors/{capability}/start`` — kick off OAuth or return a form. */
export async function startCapabilityFlow(
  capability: CapabilityName | string,
  body?: StartFlowBody,
): Promise<StartFlowResponse> {
  return apiClient.post<StartFlowResponse>(
    `/connectors/${encodeURIComponent(capability)}/start`,
    body ?? {},
  );
}

// ---------------------------------------------------------------------------
// /credentials — persist a credentials-form submission for non-OAuth vendors
// (Aruba PEC, NAS, DXF, VerifyID, Acube, InfoCert IMAP, Aruba Conservazione).
//
// Wire shape mirrors the BE endpoint at ``connectors_unified.py::submit_credentials``.
// ---------------------------------------------------------------------------

export interface SubmitCredentialsBody {
  /** Vendor slug — must be on the BE credentials-form whitelist. */
  readonly vendor: string;
  /** Field values keyed by the schema's ``key`` field. */
  readonly fields: Readonly<Record<string, string>>;
}

export interface SubmitCredentialsResponse {
  readonly connection_id: string;
  readonly vendor: string;
  readonly capability: string;
  readonly connection_key: string;
  readonly status: "connected";
  /**
   * Two-stage credentials flows (e.g. Open Banking PSD2 — the form
   * captures the VAT, the BE then mints the bank's SCA URL) attach the
   * follow-up redirect on the same response. When present, the FE must
   * navigate the user to ``oauth_url`` to complete the consent. Absent
   * for single-stage credentials flows (NAS / Aruba PEC / Acube Verify ID).
   */
  readonly flow_type?: "oauth_redirect";
  readonly oauth_url?: string;
}

/**
 * POST ``/connectors/{capability}/credentials`` — persist a credentials
 * form submission. Returns the canonical connection summary the FE list
 * consumer already knows, so the cache-invalidation flow works
 * uniformly across OAuth + credentials paths.
 */
export async function submitCapabilityCredentials(
  capability: CapabilityName | string,
  body: SubmitCredentialsBody,
): Promise<SubmitCredentialsResponse> {
  return apiClient.post<SubmitCredentialsResponse>(
    `/connectors/${encodeURIComponent(capability)}/credentials`,
    body,
  );
}

// ---------------------------------------------------------------------------
// /connections + /status
// ---------------------------------------------------------------------------

/**
 * Per-connection summary returned by
 * ``GET /connectors/{capability}/connections``. Matches the BE
 * ``CapabilityBackend._summarise_row`` output (token-free public projection).
 */
export interface ConnectionSummary {
  readonly email_address?: string;
  readonly access_mode?: "workspace" | "send_only";
  readonly connection_type?: string;
  readonly can_read?: boolean;
  readonly can_sync?: boolean;
  readonly can_send?: boolean;
  readonly can_autocomplete?: boolean;
  readonly can_reply?: boolean;
  readonly can_forward?: boolean;
  readonly can_schedule?: boolean;
  readonly conn_id: string;
  /**
   * Backend driver / enterprise connector_type. Populated by the FE
   * enterprise mapper; **absent** for white-label rows (the BE omits it).
   * For per-provider routing on public capabilities, prefer ``provider``.
   */
  readonly vendor: string;
  /**
   * User-facing SaaS provider for public multi-provider capabilities
   * (drive → google_drive/onedrive/sharepoint/notion, email → gmail/outlook,
   * messaging → slack/teams/…). Present only for those capabilities; the BE
   * deliberately omits it for white-label fiscal/banking connectors.
   */
  readonly provider?: string;
  readonly capability: string;
  readonly connection_key: string | null;
  readonly scope: string | null;
  readonly created_at: string | null;
  /**
   * OAuth ACCESS TOKEN expiry — typically refreshed silently on every
   * 401 via the refresh-token flow. **DO NOT use this to drive
   * "consent expires" UI** — the token lifetime is opaque to the
   * end-user (often <1h) and refreshes constantly. V1 audit explicitly
   * banned this pattern (``SalesforcePage.tsx`` audit note). Use
   * ``consent_expires_at`` instead for real consent surfaces (PSD2 /
   * AdE delega).
   */
  readonly expires_at: string | null;
  readonly last_used_at: string | null;
  readonly token_status: "active" | "needs_reauth" | string;
  readonly needs_reauth: boolean;
  /**
   * REAL consent expiry (different from OAuth token lifetime) — populated
   * by the BE ONLY for capabilities that issue a user-visible consent
   * with a hard deadline: PSD2 Open Banking (180 days) and AdE Cassetto
   * Fiscale delega (4-year horizon). Absent / null for every other
   * capability (Gmail, Calendar, Drive, Slack, Teams, …) because their
   * authorisation is the OAuth refresh token, which never "expires" from
   * the user's perspective.
   */
  readonly consent_expires_at?: string | null;
  readonly metadata: Record<string, unknown>;
}

interface ConnectionsListResponse {
  readonly connections: readonly ConnectionSummary[];
}

/** GET ``/connectors/{capability}/connections`` — list user's connections. */
export async function listCapabilityConnections(
  capability: CapabilityName | string,
  vendor?: string,
  purpose?: "send",
): Promise<readonly ConnectionSummary[]> {
  const params = new URLSearchParams();
  if (vendor) params.set("vendor", vendor);
  if (purpose) params.set("purpose", purpose);
  const qs = params.size ? `?${params}` : "";
  const res = await apiClient.get<ConnectionsListResponse>(
    `/connectors/${encodeURIComponent(capability)}/connections${qs}`,
  );
  return res.connections;
}

/** Overall capability state — green / amber / red light for dashboard tiles. */
export type CapabilityHealthState = "connected" | "partial" | "disconnected";

export interface CapabilityStatus {
  readonly capability: string;
  readonly state: CapabilityHealthState;
  readonly count: number;
  readonly error_count: number;
}

/** GET ``/connectors/{capability}/status`` — aggregated capability health. */
export async function getCapabilityStatus(
  capability: CapabilityName | string,
): Promise<CapabilityStatus> {
  return apiClient.get<CapabilityStatus>(`/connectors/${encodeURIComponent(capability)}/status`);
}

// ---------------------------------------------------------------------------
// DELETE /{conn_id}
// ---------------------------------------------------------------------------

export interface DeleteConnectionResponse {
  readonly success: boolean;
  readonly conn_id: string;
}

/** DELETE ``/connectors/{capability}/{conn_id}`` — disconnect. */
export async function deleteCapabilityConnection(
  capability: CapabilityName | string,
  connId: string,
  vendor?: string,
): Promise<DeleteConnectionResponse> {
  const qs = vendor ? `?vendor=${encodeURIComponent(vendor)}` : "";
  return apiClient.delete<DeleteConnectionResponse>(
    `/connectors/${encodeURIComponent(capability)}/${encodeURIComponent(connId)}${qs}`,
  );
}

// ---------------------------------------------------------------------------
// GET /connectors/open_banking/institutions
// ---------------------------------------------------------------------------

/**
 * White-label projection of one upstream bank institution.
 *
 * BE source: ``InstitutionDTO`` in ``openbanking.py`` — the response
 * deliberately omits the routing aggregator name (``fabrick`` /
 * ``enable_banking`` / ``tink``); the FE never branches on the provider.
 */
export interface InstitutionDTO {
  readonly id: string;
  readonly name: string;
  /** ISO 3166-1 alpha-2 country code (uppercase). */
  readonly country: string;
  readonly bic?: string | null;
  readonly logo_url?: string | null;
  readonly supported_features: readonly string[];
}

interface InstitutionsListResponse {
  readonly institutions: readonly InstitutionDTO[];
}

/**
 * GET ``/connectors/open_banking/institutions?country=IT&limit=50``.
 *
 * Some providers (Tink, as of May 2026) require a user-scoped token before
 * exposing institutions and will return ``[]`` here — the SPA falls back
 * to the provider's hosted picker via
 * ``POST /openbanking/connect`` with ``institution_id: ""``.
 */
export async function getOpenBankingInstitutions(
  country: string,
  limit?: number,
): Promise<readonly InstitutionDTO[]> {
  if (!country || country.length !== 2) {
    throw new Error("getOpenBankingInstitutions: invalid ISO country code");
  }
  const params = new URLSearchParams({ country: country.toUpperCase() });
  if (typeof limit === "number") {
    params.set("limit", String(limit));
  }
  const res = await apiClient.get<InstitutionsListResponse>(
    `/connectors/open_banking/institutions?${params.toString()}`,
  );
  return res.institutions;
}
